import { useNavigate } from "react-router-dom";
import { Briefcase, ArrowRight, Package } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useServiceOrders } from "@/providers/ServiceOrdersProvider";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";
import { getPackageDisplayName } from "@/domain/packageUtils";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "success" | "warning" | "destructive" | "secondary" }> = {
  pending: { label: "Pending", variant: "warning" },
  in_progress: { label: "In Progress", variant: "default" },
  completed: { label: "Completed", variant: "success" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

function ServiceOrderRow({ order }: { order: ServiceOrderDto }) {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[order.status] ?? { label: order.status, variant: "secondary" as const };

  return (
    <button
      onClick={() => navigate(`/service-orders/${order.id}/overview`)}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <Briefcase className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{order.customProjectName || order.project.name}</p>
          <p className="text-xs text-muted-foreground">{getPackageDisplayName(order.packageId)}</p>
        </div>
      </div>
      <Badge variant={config.variant} className="shrink-0">{config.label}</Badge>
    </button>
  );
}

export function ServiceOrdersCard() {
  const navigate = useNavigate();
  const { serviceOrders, isLoading } = useServiceOrders();

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base font-semibold">Service Orders</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/service-orders")} className="text-xs">
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : serviceOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Package className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-3">No service orders yet</p>
            <Button variant="outline" size="sm" onClick={() => navigate("/service-orders")}>
              Get started
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            {serviceOrders.map((order) => (
              <ServiceOrderRow key={order.id} order={order} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
